import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";

import {
  agentModelsQueryOptions,
  agentUsageQueryOptions,
  defaultChoices,
  useAgentModels,
} from "@/agent/chat/models";
import { ModelName } from "@/agent/chat/ProviderMark";
import { ORGANIZATION_SETTINGS_QUERY_KEY } from "@/hooks/queries";
import {
  getOrganizationSettingsErrorMessage,
  organizationSettingsService,
  type OrganizationSettings,
  type UpdateOrganizationSettingsRequest,
} from "@/services/organizationSettings";
import TrialLocked from "@/components/trial/TrialLocked";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const FOREVER = "forever";
const RETENTION_DAYS = [30, 90, 180, 365];

function retentionLabel(days: number): string {
  return days === 365 ? "1 year" : `${days} days`;
}

/** The organization fields this card saves (trial accounts may be locked out of each). */
const AI_FIELDS = ["agent_default_model", "chat_retention_days", "agent_show_usage"] as const;

const toRetention = (value: string): number | null => (value === FOREVER ? null : Number(value));
const fromRetention = (days: number | null): string => (days == null ? FOREVER : String(days));

interface AiAssistantSectionProps {
  settings: OrganizationSettings | undefined;
  loading: boolean;
}

/**
 * The organization's default assistant model, how long assistant chats are kept, and whether the
 * assistant on WhatsApp and Telegram sees what users had open on the web app.
 */
const AiAssistantSection = ({ settings, loading }: AiAssistantSectionProps) => {
  const queryClient = useQueryClient();
  const catalog = useAgentModels();
  const [model, setModel] = useState<string | null>(null);
  const [retention, setRetention] = useState<string | null>(null);
  const [showUsage, setShowUsage] = useState<boolean | null>(null);

  const changes: UpdateOrganizationSettingsRequest = {};
  if (settings && model != null && model !== settings.agent_default_model) {
    changes.agent_default_model = model;
  }
  if (settings && retention != null && toRetention(retention) !== settings.chat_retention_days) {
    changes.chat_retention_days = toRetention(retention);
  }
  if (settings && showUsage != null && showUsage !== settings.agent_show_usage) {
    changes.agent_show_usage = showUsage;
  }

  const mutation = useMutation({
    mutationFn: () => organizationSettingsService.update(changes),
    onSuccess: (data) => {
      queryClient.setQueryData(ORGANIZATION_SETTINGS_QUERY_KEY, data);
      setModel(null);
      setRetention(null);
      setShowUsage(null);
      void queryClient.invalidateQueries({ queryKey: agentModelsQueryOptions.queryKey });
      void queryClient.invalidateQueries({ queryKey: agentUsageQueryOptions.queryKey });
    },
  });

  // Saved on toggle, apart from Save changes: a trial organization can't save the model, but it
  // can always turn this off.
  const desktopMutation = useMutation({
    mutationFn: (on: boolean) => organizationSettingsService.update({ agent_desktop_context: on }),
    onSuccess: (data) => queryClient.setQueryData(ORGANIZATION_SETTINGS_QUERY_KEY, data),
  });

  const locked = (field: string) => settings?.trial_locked_fields.includes(field) ?? false;
  const busy = loading || mutation.isPending;
  const savedRetention = settings?.chat_retention_days ?? null;
  const retentionOptions =
    savedRetention == null || RETENTION_DAYS.includes(savedRetention)
      ? RETENTION_DAYS
      : [...RETENTION_DAYS, savedRetention].sort((a, b) => a - b);
  // Never undefined: an empty value keeps the select controlled while the settings load.
  const selectedModel = model ?? settings?.agent_default_model ?? "";

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI Assistant</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {(mutation.isError || desktopMutation.isError) && (
          <Alert variant="destructive">
            <AlertDescription>
              {getOrganizationSettingsErrorMessage(mutation.error ?? desktopMutation.error)}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="ai-default-model">Default model</Label>
          <TrialLocked locked={locked("agent_default_model")}>
            <Select value={selectedModel} onValueChange={setModel} disabled={busy || !catalog}>
              <SelectTrigger id="ai-default-model" className="w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {catalog &&
                  defaultChoices(catalog).map((option) => (
                    <SelectItem key={option.ref} value={option.ref}>
                      <ModelName model={option} />
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </TrialLocked>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ai-chat-retention">Keep assistant chats</Label>
          <TrialLocked locked={locked("chat_retention_days")}>
            <Select
              value={retention ?? fromRetention(savedRetention)}
              onValueChange={setRetention}
              disabled={busy}
            >
              <SelectTrigger id="ai-chat-retention" className="w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FOREVER}>Forever</SelectItem>
                {retentionOptions.map((days) => (
                  <SelectItem key={days} value={String(days)}>
                    {retentionLabel(days)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </TrialLocked>
        </div>

        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="ai-show-usage">Show AI usage</Label>
          <TrialLocked locked={locked("agent_show_usage")} inline side="left">
            <Switch
              id="ai-show-usage"
              checked={showUsage ?? settings?.agent_show_usage ?? false}
              onCheckedChange={setShowUsage}
              disabled={busy}
            />
          </TrialLocked>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5 pr-4">
            <Label htmlFor="ai-desktop-context">Share desktop context with WhatsApp/Telegram</Label>
            <p className="text-xs text-muted-foreground">
              The assistant on WhatsApp and Telegram knows which applications a user had open on
              their computer and their recent activity, so it can tell which one they mean. Each
              user can also turn this off for themselves.
            </p>
          </div>
          <Switch
            id="ai-desktop-context"
            checked={settings?.agent_desktop_context ?? true}
            onCheckedChange={(on) => desktopMutation.mutate(on)}
            disabled={busy || desktopMutation.isPending}
          />
        </div>

        {/* Locked while any of the card's fields is: a save would be refused. */}
        <TrialLocked locked={AI_FIELDS.some(locked)} inline>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={busy || Object.keys(changes).length === 0}
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save changes
          </Button>
        </TrialLocked>
      </CardContent>
    </Card>
  );
};

export default AiAssistantSection;
